import React, { useState } from "react";
import { IMPORT_FILE_ACCEPT, dayPeopleFor, formatDay } from "../api/shape.js";
import { usePhone } from "../phone/usePhone.js";
import PhoneToday from "../phone/PhoneToday.jsx";
import AddInspection from "./day/AddInspection.jsx";
import { useAssign, useUploadPicker } from "./day/useDayActions.js";
import { ImportPanel } from "./Plan.jsx";

/**
 * Today by the yard's day (Day redesign phase 1, DEC-CHAT-20261013-201:
 * mockups/cockpit-v3/TodayDay.dc.html). `day` is shape.js#shapeDayScreen's
 * output (live and fixture alike). The headline counts sides per D8; the lines
 * the yard asked for sit under "Not assigned yet" first, then under the site
 * manager and the assistant inspector, each in time order.
 */

function AssignButtons({ line, lane, people, pending, onAssign }) {
  const { sm, assistants } = people;
  const toAssistant =
    assistants.length > 1 ? (
      <select
        className="day-assist-pick"
        aria-label={`Assign ${line.item} to an assistant inspector`}
        disabled={pending}
        value=""
        onChange={(e) => e.target.value && onAssign(line, e.target.value)}
      >
        <option value="">To assistant…</option>
        {assistants.map((a) => <option key={a.upn} value={a.upn}>{a.name}</option>)}
      </select>
    ) : (
      <button
        type="button"
        className="btn"
        disabled={pending || assistants.length === 0}
        title={assistants.length === 0 ? "No assistant inspector on this project's roster" : undefined}
        onClick={() => onAssign(line, assistants[0].upn)}
      >
        {pending ? "…" : "To assistant"}
      </button>
    );
  const toMe = (
    <button
      type="button"
      className="btn"
      disabled={pending || !sm}
      title={!sm ? "You are not an inspector on this project's roster" : undefined}
      onClick={() => onAssign(line, sm.upn)}
    >
      {lane === "theirs" ? "To site manager" : "To me"}
    </button>
  );
  if (lane === "unassigned") return <div className="acts">{toMe}{toAssistant}</div>;
  const toNobody = (
    <button type="button" className="btn" disabled={pending} onClick={() => onAssign(line, "")}>
      {pending ? "…" : "Move to not assigned"}
    </button>
  );
  return <div className="acts">{lane === "mine" ? toAssistant : toMe}{toNobody}</div>;
}

function DayRow({ line, lane, people, pending, result, onAssign, showCode }) {
  const open = line.open;
  const withActions = open;
  return (
    <div className={`${line.cls}${lane === "unassigned" ? " open" : open ? " wide" : ""}`} data-testid="day-line" data-lane={lane}>
      <span className="time">{line.timeText}</span>
      <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
        <span className="ttl">
          {line.item}
          {line.repair ? <span className="chip acc">REPAIR CHECK</span> : null}
        </span>
        <span className="sub">{showCode ? `${line.projectCode} · ${line.place}` : line.place}</span>
        {line.repair && line.defectsDesktop ? <span className="dft">{line.defectsDesktop}</span> : null}
        {line.sides.filter((s) => s.closed).map((s) => (
          <span key={s.scheduleRef} className="sub" data-testid="day-closed-side">
            {s.label !== "—" ? `${s.label} ` : ""}{s.carried ? `carried to ${s.carriedTo ? formatDay(`${s.carriedTo}T00:00:00Z`) : "the next working day"}` : `not attended: ${s.notAttendedReason}`}
          </span>
        ))}
        {result ? <span className="day-msg" role="alert">{result.message}</span> : null}
      </div>
      <div className="sides">
        {line.sides.map((s) => (
          <span key={s.scheduleRef} className={s.cls} title={s.carried ? "Carried to the next working day" : s.notAttended ? `Not attended: ${s.notAttendedReason}` : undefined}>{s.label}</span>
        ))}
      </div>
      {withActions ? <AssignButtons line={line} lane={lane} people={people(line.projectCode)} pending={pending} onAssign={onAssign} /> : <span />}
    </div>
  );
}

function Lane({ title, block, lane, pendingKey, results, ...rest }) {
  return (
    <section className="day-lane" data-testid={`day-lane-${lane}`}>
      <h2 className="sec"><span>{title}</span><span className="mono">{block.recorded} of {block.sides} sides recorded</span></h2>
      <div className="list">
        {block.lines.length === 0 ? <div className="r" style={{ gridTemplateColumns: "1fr" }}><span className="sub">Nothing assigned</span></div> : null}
        {block.lines.map((line) => <DayRow key={line.key} line={line} lane={lane} pending={pendingKey === line.key} result={results[line.key]} {...rest} />)}
      </div>
    </section>
  );
}

function EmptyDay({ day, upload, onAdd }) {
  return (
    <div className="day-empty" data-testid="day-empty">
      <h2>No yard list for today yet</h2>
      <p>When the yard&apos;s QC sends today&apos;s inspection list on WeChat, save the file and upload it here.</p>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className="btn fill" data-testid="today-upload-button" onClick={upload.open}>Upload today&apos;s list</button>
        <button type="button" className="btn" onClick={onAdd}>Add one inspection</button>
      </div>
      {day.expectedCount ? (
        <div>
          <div className="sec">Expected today</div>
          <p className="day-note">About {day.expectedCount} inspections, from the yard&apos;s block schedule. These are not requested until they appear on the daily list.</p>
        </div>
      ) : null}
      {day.carried.length ? (
        <div data-testid="day-carried">
          <div className="sec">Still open from yesterday</div>
          <p className="day-note">
            {day.carried.length} {day.carried.length === 1 ? "inspection" : "inspections"} not recorded: {day.carried.map((c) => c.text).join("; ")}. Carried to today; it merges with the same line when today&apos;s list arrives.
          </p>
        </div>
      ) : null}
    </div>
  );
}

export default function Today({
  day,
  errors = [],
  notice = null,
  lanes = [],
  me = "",
  projectCodes = [],
  onNavigate,
  onAssign,
  onAdd,
  canImport = false,
  importPanel = null,
  onImportRefused,
  onImportFileChosen,
  onImportCommit,
  onImportDismiss,
  onImportToggleRow,
  onImportToggleOwnerOnly,
  onImportSelectAllShown,
  onImportClearSelection,
  initialAddOpen = false,
  ...phoneProps
}) {
  const phone = usePhone();
  const [addOpen, setAddOpen] = useState(initialAddOpen);
  const { pendingKey, results, assign } = useAssign(onAssign);
  const upload = useUploadPicker({ canImport, onRefused: onImportRefused, onFileChosen: onImportFileChosen });
  const people = (code) => dayPeopleFor(code, lanes, me);

  if (phone) {
    return (
      <PhoneToday
        day={day}
        errors={errors}
        notice={notice}
        people={people}
        pendingKey={pendingKey}
        results={results}
        onAssign={assign}
        onAdd={onAdd}
        projectCodes={projectCodes}
        upload={upload}
        importPanel={importPanel}
        onImportCommit={onImportCommit}
        onImportDismiss={onImportDismiss}
        onNavigate={onNavigate}
        addOpen={addOpen}
        setAddOpen={setAddOpen}
        {...phoneProps}
      />
    );
  }

  const rowProps = { people, onAssign: assign, showCode: day.multiProject };
  const noteParts = ["Counted per side", day.importedAt ? `yard's daily list uploaded ${day.importedAt}` : "no yard daily list uploaded yet", day.unscheduledText.charAt(0).toLowerCase() + day.unscheduledText.slice(1)];
  const addCodes = day.projectCodes.length ? day.projectCodes : projectCodes;

  return (
    <div className="day-page" data-testid="today-day" data-day-date={day.date}>
      <div className="day-head">
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div className="h1">Today</div>
          <div className="day-figures">
            <span className="req">{day.requestedText}</span>
            <span className="rec">{day.recordedText}</span>
            <span className="togo">{day.toGoText}</span>
            {day.postponedText ? <span className="day-note">{day.postponedText}</span> : null}
            {day.closedText ? <span className="day-note" data-testid="day-closed-figures">{day.closedText}</span> : null}
          </div>
        </div>
        <div className="day-progress">
          <div className="day-bar" aria-hidden="true"><div style={{ width: day.pct }} /></div>
          <span className="day-note" data-testid="day-note">{noteParts.join(" · ")}</span>
        </div>
        <button type="button" className="btn" data-testid="today-add-button" onClick={() => setAddOpen((v) => !v)}>Add one inspection</button>
        <button type="button" className="btn" data-testid="today-upload-button" onClick={upload.open}>Upload a new list</button>
        {day.canEndDay ? <button type="button" className="btn fill" data-testid="today-eod-button" onClick={() => onNavigate && onNavigate("endofday")}>End of day</button> : null}
        <input ref={upload.ref} type="file" accept={IMPORT_FILE_ACCEPT} data-testid="today-import-file-input" style={{ display: "none" }} onChange={upload.onChange} />
      </div>

      {notice ? <div className="day-msg" role="alert" data-testid="today-notice">{notice}</div> : null}

      {errors.length > 0 ? (
        <div className="list" data-testid="today-errors">
          {errors.map((e) => (
            <div className="r" key={e.code} style={{ gridTemplateColumns: "1fr" }}>
              <span className="ttl" style={{ color: "var(--bad)" }}>{e.code}: unavailable &mdash; {e.error}</span>
            </div>
          ))}
        </div>
      ) : null}

      {addOpen ? <AddInspection projects={addCodes} date={day.date} onSave={onAdd} onClose={() => setAddOpen(false)} /> : null}

      {importPanel ? (
        <ImportPanel
          importPanel={importPanel}
          onCommit={onImportCommit}
          onDismiss={onImportDismiss}
          onToggleRow={onImportToggleRow}
          onToggleOwnerOnly={onImportToggleOwnerOnly}
          onSelectAllShown={onImportSelectAllShown}
          onClearSelection={onImportClearSelection}
        />
      ) : null}

      {!day.hasList ? (
        <EmptyDay day={day} upload={upload} onAdd={() => setAddOpen(true)} />
      ) : (
        <>
          {day.unassigned.lines.length > 0 ? (
            <section data-testid="day-lane-unassigned" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <h2 className="sec warn"><span>Not assigned yet</span><span className="mono">{day.unassigned.lines.length} {day.unassigned.lines.length === 1 ? "line" : "lines"}</span></h2>
              <div className="list warn">
                {day.unassigned.lines.map((line) => (
                  <DayRow key={line.key} line={line} lane="unassigned" pending={pendingKey === line.key} result={results[line.key]} {...rowProps} />
                ))}
              </div>
            </section>
          ) : null}
          <div className="day-two">
            <Lane title="Site manager" lane="mine" block={day.mine} pendingKey={pendingKey} results={results} {...rowProps} />
            <Lane title="Assistant inspector" lane="theirs" block={day.theirs} pendingKey={pendingKey} results={results} {...rowProps} />
          </div>
          {day.carried.length ? (
            <section data-testid="day-carried" className="day-lane">
              <h2 className="sec"><span>Not recorded yesterday</span><span className="mono">{day.carried.length}</span></h2>
              <div className="list">
                {day.carried.map((c) => (
                  <div className="r" key={c.key} style={{ gridTemplateColumns: "1fr" }}><span className="sub">{day.multiProject ? `${c.projectCode} \u00b7 ` : ""}{c.text}</span></div>
                ))}
              </div>
            </section>
          ) : null}
          <p className="day-foot">Each side counts once. P and S of one block share a line; a green side is recorded. A line with no side on the yard&apos;s list counts as one. Repair checks show the defects we found last time.</p>
        </>
      )}
    </div>
  );
}
